import { useEffect, type ComponentType } from 'react';
import { routeHash } from '@/app/router';
import { listProfiles } from '@/game/profiles';
import { cx } from '@/lib/cx';
import s from './debug.module.css';
import { AudioLabPanel } from './AudioLabPanel';
import { DevicePanel } from './DevicePanel';
import { ProfilesPanel } from './ProfilesPanel';
import { ProgressPanel } from './ProgressPanel';
import { QaSessionsPanel } from './QaSessionsPanel';
import { RenderPanel } from './RenderPanel';
import { ScreensPanel } from './ScreensPanel';
import { StoragePanel } from './StoragePanel';
import { ToastProvider } from './ui';
import { VoicePanel } from './VoicePanel';

interface Tab {
  id: string;
  label: string;
  icon: string;
  panel: ComponentType;
}

const TABS: readonly Tab[] = [
  { id: 'profiles', label: 'Profiles', icon: '◉', panel: ProfilesPanel },
  { id: 'progress', label: 'Progress', icon: '★', panel: ProgressPanel },
  { id: 'screens', label: 'Screens', icon: '▤', panel: ScreensPanel },
  { id: 'voice', label: 'Voice', icon: '♪', panel: VoicePanel },
  { id: 'audio', label: 'Audio lab', icon: '∿', panel: AudioLabPanel },
  { id: 'render', label: 'Render', icon: '▶', panel: RenderPanel },
  { id: 'qa', label: 'QA sessions', icon: '●', panel: QaSessionsPanel },
  { id: 'storage', label: 'Storage', icon: '⛁', panel: StoragePanel },
  { id: 'device', label: 'Device', icon: 'ⓘ', panel: DevicePanel },
];

/**
 * `#/debug/<tab>`: the debug tools for development and QA, opened from the grown-ups' settings.
 * A tool, not part of the game: its own dense, neutral design (debug.module.css), a sidebar on wide
 * screens and a tab strip on narrow ones. Each tab has its own address, so a reload (switching
 * profiles reloads) comes back to it.
 */
export function DebugPage({ tab }: { tab?: string | undefined }) {
  const current = TABS.find((t) => t.id === tab) ?? TABS[0];
  const { active, profiles } = listProfiles();
  const player = profiles.find((p) => p.id === active)?.name;
  useEffect(() => {
    document.title = `Debug · ${current?.label ?? ''} · Foe Names`;
    return () => {
      document.title = 'Foe Names';
    };
  }, [current]);
  // Keep the current tab in view in the narrow tab strip.
  useEffect(() => {
    document
      .querySelector('[aria-current="page"]')
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [current]);
  if (!current) return null;
  const Panel = current.panel;

  return (
    <ToastProvider>
      <div className={s.shell}>
        <header className={s.head}>
          <div className={s.brand}>
            Foe Names <span>· Debug</span>
          </div>
          <div className={s.headInfo}>
            {player && (
              <a
                className={cx(s.badge, s.badgeAccent)}
                href={routeHash({ page: 'debug', tab: 'profiles' })}
                title="Active profile"
              >
                ◉ {player}
              </a>
            )}
            <span className={cx(s.badge, s.hideNarrow, s.mono)} title={`Built ${__BUILD_TIME__}`}>
              {__APP_VERSION__}
            </span>
          </div>
          <a className={cx(s.btn, s.small)} href={routeHash({ page: 'elevator' })}>
            ← Game
          </a>
        </header>

        <nav className={s.nav} aria-label="Debug sections">
          <ul>
            {TABS.map((t) => (
              <li key={t.id}>
                <a
                  className={s.navLink}
                  href={routeHash({ page: 'debug', tab: t.id })}
                  aria-current={t.id === current.id ? 'page' : undefined}
                >
                  <span className={s.navIcon} aria-hidden>
                    {t.icon}
                  </span>
                  {t.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <main className={s.main} id="debug-main">
          <h1 className={s.visuallyHidden}>{current.label}</h1>
          <Panel />
        </main>
      </div>
    </ToastProvider>
  );
}
